import { useEffect, useRef, useState } from "react";
import { createFormValidation } from "../../../helpers/formValidationHelper";
import { FormControlElement, ILanguage } from "../../../models/Interfaces";
import AppModal from "../../components/common/AppModal";
import { faSave } from "@fortawesome/free-solid-svg-icons";
import { Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import { Core } from "@form-validation/bundle/full";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { Excluded } from '@form-validation/plugin-excluded';
import styles from "./siteLanguages.module.scss";

interface IAddUpdateLanguageModal {
  language: ILanguage;
  onClose: () => void;
  onSave: (language: ILanguage) => void;
}

const AddUpdateLanguageModal = (props: IAddUpdateLanguageModal) => {
  const [selectedLanguage, setSelectedLanguage] = useState<ILanguage | null>(null);

  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);
  
  const handleInputChange = (event: React.ChangeEvent<FormControlElement>) : void => {
    const { value, name } = event.target;

    if (name) {
      setSelectedLanguage(prev => ({
        ...prev as ILanguage,
        [name]: value
      }));
    }
  };

  const handleCheckChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    const { checked, name } = event.target;

    if (name) {
      setSelectedLanguage(prev => ({
        ...prev as ILanguage,
        [name]: checked
      }));
    }
  };

  const createValidation = () : void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        fields: {
          Language: {
            validators: {
              notEmpty: { message: "Language name is required" }
            }
          },
          ShortCode: {
            validators: {
              notEmpty: { message: "Region short code is required" }
            }
          }
        },
        plugins: {
          bootstrap5: new Bootstrap5(),
          trigger: new Trigger(),
          excluded: new Excluded(),
        }
      });
    }
  };

  const saveLanguage = async () : Promise<void> => {
    if (fvRef.current && props.onSave && selectedLanguage) {
      const validres = await fvRef.current.validate();

      if (validres === "Valid") {
        props.onSave(selectedLanguage);
      }
    }
  };

  useEffect(() => {
    setSelectedLanguage(props.language);
  }, [props.language]);

  useEffect(() => {
    if (formRef.current) {
      createValidation();
    }
  }, []);

  return (
    <>
      <AppModal
        onClose={props.onClose}
        onDataAction={saveLanguage}
        size="md"
        showButtons={true}
        dataActionButtonText="Save Language"
        title="Language Details"
        actionButtonIcon={faSave}
        actionButtonVariant="success"
        cancelButtonVariant="primary">
        
        <Form id="LanguageForm" ref={formRef} className={styles.modalForm}>
          <Row className="g-3">
            <Col md={12}>
              <FormLabel htmlFor="Language">Language</FormLabel>
              <FormControl
                type="text"
                id="Language"
                name="Language"
                value={selectedLanguage?.Language ?? ""}
                onChange={handleInputChange}
                placeholder="language name"
              />
            </Col>
            <Col md={12}>
              <FormLabel htmlFor="ShortCode">Short Code (browser region code)</FormLabel>
              <FormControl
                type="text"
                id="ShortCode"
                name="ShortCode"
                value={selectedLanguage?.ShortCode ?? ""}
                onChange={handleInputChange}
                placeholder="browser region short code e.g. en-gb"
              />
            </Col>
            <Col md={12}>
              <FormLabel htmlFor="Enabled">Enabled</FormLabel>
              <label className={styles.toggleCard} htmlFor="Enabled">
                <div>
                  <div className={styles.toggleTitle}>Active language</div>
                  <div className={styles.toggleSummary}>Make this language available across pages, menus, and other site content.</div>
                </div>
                <FormCheckInput
                  className={styles.toggleInput}
                  id="Enabled"
                  name="Enabled"
                  title="Disable/enable language"
                  checked={selectedLanguage?.Enabled ?? false}
                  onChange={handleCheckChange}
                />
              </label>
            </Col>
          </Row>
        </Form>
      </AppModal>
    </>
  );
}

export default AddUpdateLanguageModal;
